import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";

interface TermsAcceptanceDialogProps {
  open: boolean;
  onAccept: () => Promise<void>;
  // true when a professional advisor is logged in on the client's behalf
  isProfessional?: boolean;
  clientName?: string | null;
  termsShared?: boolean;
  onShareTerms?: () => Promise<void>;
}

export const TermsAcceptanceDialog = ({
  open,
  onAccept,
  isProfessional = false,
  clientName,
  termsShared = false,
  onShareTerms,
}: TermsAcceptanceDialogProps) => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const displayName = clientName || "your client";

  const handleAccept = async () => {
    setLoading(true);
    try {
      await onAccept();
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!onShareTerms) return;
    setSharing(true);
    try {
      await onShareTerms();
    } catch (error) {
      console.error("Error sharing terms:", error);
    } finally {
      setSharing(false);
    }
  };

  const termsLink = (
    <Link
      component="a"
      href="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/User+Terms+of+Service.pdf"
      target="_blank"
    >
      Terms of Service
    </Link>
  );

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Typography variant="h4">Terms of Service</Typography>
          <Typography variant="body2" color="text.primary">
            {isProfessional ? (
              <>
                You are acting on behalf of {displayName}. Please review and
                accept the {termsLink} to continue using the application on
                their behalf.
              </>
            ) : (
              <>
                Please review and accept the {termsLink} to continue using the
                application.
              </>
            )}
          </Typography>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              ml: -1,
            }}
          >
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <Typography color="text.secondary" variant="body2">
              {isProfessional
                ? `I confirm I am authorized to accept the Terms of Service on behalf of ${displayName}, and I have read, understand, and agree to be bound by them on their behalf`
                : "I have read, understand, and agree to be bound by the Terms of Service"}
            </Typography>
          </Box>
          <LoadingButton
            fullWidth
            variant="contained"
            disabled={!accepted}
            loading={loading}
            onClick={handleAccept}
          >
            Continue
          </LoadingButton>
          {isProfessional && onShareTerms && (
            <Button
              size="small"
              variant="text"
              disabled={termsShared || sharing}
              onClick={handleShare}
              sx={{ alignSelf: "center" }}
            >
              {termsShared
                ? `Terms sent to ${displayName}`
                : `Email the terms to ${displayName}`}
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
